import React, { useReducer, useEffect, useRef, forwardRef } from "react";
import { formatDistanceToNow } from "date-fns";
import { useWebSocket } from "@/context/WebSocketContext";
import { Avatar } from "@/components/ui/Avatar";
import Logo from "../Logo";
import WorkerAvatar from "../worker/WorkerAvatar";

// Define the activity log type
export type ActivityLogItem = {
  id: string;
  description?: string;
  timestamp: string;
  worker_id?: string;
  metadata?: Record<string, any>;
  actor?: { name?: string };
  [key: string]: any;
};

// Define the state type
type State = {
  activityLogs: ActivityLogItem[];
  loading: boolean;
  error: string | null;
  refreshCounter: number;
};

// Define the action types
type Action =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: ActivityLogItem[] }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "REFRESH" }
  | { type: "ADD_ACTIVITY"; payload: ActivityLogItem };

// Define the reducer function
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, activityLogs: action.payload };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "REFRESH":
      return {
        ...state,
        loading: true,
        refreshCounter: state.refreshCounter + 1,
      };
    case "ADD_ACTIVITY":
      // Add new activity log to the beginning of the list
      return {
        ...state,
        activityLogs: [action.payload, ...state.activityLogs],
      };
    default:
      return state;
  }
}

interface ActivityLogProps {
  entityId: string;
  entityType?: string;
  fetchActivityLogs?: (
    entityId: string,
    limit?: number
  ) => Promise<ActivityLogItem[]>;
  websocketMessageType?: string;
  title?: string;
  limit?: number;
}

// Define the ref type
export interface ActivityLogRefType {
  refreshActivityLogs: () => void;
}

export const ActivityLog = forwardRef<ActivityLogRefType, ActivityLogProps>(
  function ActivityLog(
    {
      entityId,
      fetchActivityLogs,
      websocketMessageType = "activity_updated",
      limit = 50,
    },
    ref
  ) {
    const { lastMessage } = useWebSocket();

    // Initial state
    const initialState: State = {
      activityLogs: [],
      loading: true,
      error: null,
      refreshCounter: 0,
    };

    // Use reducer instead of multiple useState calls
    const [state, dispatch] = useReducer(reducer, initialState);

    // Refs to track component lifecycle and prevent duplicate requests
    const isMounted = useRef(true);
    const isLoadingRef = useRef(false);

    // Fetch activity logs on mount and when entityId, limit, or fetchActivityLogs changes
    useEffect(() => {
      if (!fetchActivityLogs) return;
      let cancelled = false;
      const fetchData = async () => {
        dispatch({ type: "FETCH_START" });
        try {
          const logs = await fetchActivityLogs(entityId, limit);
          if (!cancelled) {
            dispatch({ type: "FETCH_SUCCESS", payload: logs });
          }
        } catch (err) {
          if (!cancelled) {
            dispatch({
              type: "FETCH_ERROR",
              payload: err instanceof Error ? err.message : String(err),
            });
          }
        }
      };
      fetchData();
      return () => {
        cancelled = true;
      };
    }, [entityId, limit, fetchActivityLogs]);

    // Listen for real-time activity updates via WebSocket
    useEffect(() => {
      // Get the message for the specific channel
      const message: any = lastMessage.get(websocketMessageType);

      if (
        message &&
        message.operation === "insert" &&
        message.full_document &&
        message.full_document.pod_instance_id === entityId
      ) {
        const fullDoc = message.full_document;

        // Transform full_document to ActivityLogItem
        const newActivity: ActivityLogItem = {
          id: fullDoc._id,
          description: fullDoc.description,
          timestamp: fullDoc.timestamp,
          worker_id: fullDoc.assignee?.id,
          metadata: fullDoc.metadata,
          actor: fullDoc.assignee ? { name: fullDoc.assignee.name } : undefined,
        };

        if (isMounted.current) {
          console.log(
            "[ActivityLog] Adding new activity via WebSocket:",
            newActivity
          );
          dispatch({ type: "ADD_ACTIVITY", payload: newActivity });
        }
      }
    }, [lastMessage, entityId, websocketMessageType]);

    if (state.loading) {
      return (
        <div className="p-4 flex h-full w-full text-slate-600 dark:text-slate-400">Loading activity logs...</div>
      );
    }

    if (state.error) {
      return <div className="p-4 text-red-500">Error: {state.error}</div>;
    }

    if (state.activityLogs.length === 0) {
      return (
        <div className="p-4 h-full flex items-center justify-center text-gray-500 dark:text-gray-400 font-mono w-full">
          No activity logs available
        </div>
      );
    }

    return (
      <div className="p-4 flex h-full w-full">
        <div className="flow-root w-full">
          <ul role="list" className="-mb-8">
            {state.activityLogs.map((log, logIdx) => (
              <li key={log.id}>
                <div className="relative pb-4">
                  {logIdx !== state.activityLogs.length - 1 ? (
                    <span
                      className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-100 dark:bg-gray-700"
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="flex h-8 w-8 items-center justify-center ">
                        {log.worker_id === "ani" ? (
                          <Logo iconOnly className="h-8 w-8" />
                        ) : (
                          <WorkerAvatar
                            seed={log.actor.name || "default"}
                            size={32}
                          />
                        )}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 justify-between space-x-4">
                      <div>
                        {log.description && (
                          <p className="font-normal text-sm text-gray-900 dark:text-gray-100">
                            {log.description}
                          </p>
                        )}
                        <p className="text-xs mt-1 text-gray-600 dark:text-gray-300">
                          {log.actor?.name && (
                            <span className="font-mono text-gray-500 dark:text-gray-400">
                              {log.actor.name}
                            </span>
                          )}
                        </p>

                        {/* {log.metadata && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {Object.entries(log.metadata).map(
                              ([key, value]) => (
                                <div key={key} className="font-mono">
                                  <span className="font-medium">{key}:</span>{" "}
                                  {typeof value === "string"
                                    ? value
                                    : JSON.stringify(value)}
                                </div>
                              )
                            )}
                          </div>
                        )} */}
                      </div>
                      <div className="text-right text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">
                        <time dateTime={new Date(log.timestamp).toISOString()}>
                          {formatDistanceToNow(new Date(log.timestamp), {
                            addSuffix: true,
                          })}
                        </time>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
);
